import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@material-tailwind/react";
import PaymentService from "../../Services/payment.services";
import { sidebarStore } from "../store/SidebarStore";

type RecordState = "recording" | "recorded" | "error";

export default function ZeroPaymentCard({
  insuranceCompanies,
}: {
  insuranceCompanies: string[];
}) {
  const navigate = useNavigate();
  const [recordState, setRecordState] = useState<RecordState>("recording");
  const hasRecorded = useRef(false);

  useEffect(() => {
    // The zero payment is recorded automatically, with no action from the user.
    if (hasRecorded.current) return;
    hasRecorded.current = true;
    recordZeroPayment();
  }, []);

  const recordZeroPayment = async () => {
    setRecordState("recording");

    let postPaymentPayload = {
      isAnnualPayment: true,
      totalAnnualPrice: 0,
      pricePerMonth: 0,
      shouldCreateFirstPayment: true,
      isFirstPaymentPaid: true,
      IntelliPayTransactionId: "",
      IntelliPayCustId: "",
    };

    const result = await PaymentService.PostPayment(postPaymentPayload);

    if (!result || !result.paymentId) {
      setRecordState("error");
      return;
    }

    // No next payment is scheduled: credentialing is pay as you go, not a
    // subscription.
    sidebarStore.setCanCreateNewRequest(true);
    setRecordState("recorded");
  };

  const retry = () => {
    recordZeroPayment();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="container mx-auto px-1 col-span-1 shadow-2xl">
        <div style={{ backgroundColor: "white" }}>
          <div className="p-5 bg-opacity-75">
            <p className="text-4xl font-medium text-black my-7 text-center">
              No Payment Due
            </p>
            <h1 className="text-6xl font-medium text-black my-7 text-center">
              $0.00
            </h1>

            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full bg-white table-auto rounded-lg shadow-md">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="py-2 px-4 border-t border-gray-300 text-left font-semibold">
                      Insurance Company
                    </th>
                    <th className="py-2 px-4 border-t border-gray-300 text-left font-semibold">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {insuranceCompanies.map((company: string, index: number) => (
                    <tr key={index} className="border-t">
                      <td className="py-2 px-4 text-gray-700">{company}</td>
                      <td className="py-2 px-4 text-gray-900">$0.00</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-gray-50">
                    <td className="py-2 px-4 text-right font-semibold">Total</td>
                    <td className="py-2 px-4">$0.00</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-black-200 text-opacity-70">
              There is nothing to pay for this credentialing request. Each of
              your insurance requests is $0.00, so no payment information is
              needed.
            </p>

            <div className="mt-5">
              {recordState === "error" ? (
                <>
                  <Button
                    className="w-full py-3 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white"
                    onClick={retry}
                  >
                    Try Again
                  </Button>
                  <p className="mt-3 text-center text-red-600">
                    Something went wrong. Please try again.
                  </p>
                </>
              ) : (
                <Button
                  className={`w-full py-3 rounded-lg ${
                    recordState === "recorded"
                      ? "bg-indigo-500 hover:bg-indigo-600 text-white"
                      : "bg-blue-grey-50 text-gray-500 cursor-not-allowed"
                  }`}
                  disabled={recordState !== "recorded"}
                  onClick={() => navigate("/dashboard", { replace: true })}
                >
                  Continue to Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
